import { Box, Text, Button, useMediaQuery } from "@chakra-ui/react";
import { SubscriptionPlanInterface } from "./PlanDetails";

interface PlanCardProps {
    plan: SubscriptionPlanInterface;
    isSelected: boolean;
    onSelect: (plan: SubscriptionPlanInterface) => void;
}

const PlanCard = ({ plan, isSelected, onSelect }: PlanCardProps) => {
    const [isMobile] = useMediaQuery("(max-width: 480px)");
    const color = plan.themeColor || "teal";

    return (
        <Box
            border={isSelected ? `2px solid ${color}` : "1px solid #e0e0e0"}
            borderRadius="16px"
            p={isMobile ? "20px" : "32px"}
            bg="white"
            cursor="pointer"
            onClick={() => onSelect(plan)}
            boxShadow={isSelected ? "lg" : "sm"}
            transition="all 0.2s"
            _hover={{ boxShadow: "md" }}
            maxW={isMobile ? "full" : "340px"}
            minW={isMobile ? "full" : "300px"}
            w={isMobile ? "full" : "340px"}
        >
            {/* Title */}
            <Text
                fontFamily="Poppins"
                fontSize={isMobile ? "20px" : "24px"}
                fontWeight="400"
                textAlign="center"
                color="#333"
            >
                {plan.planName}
            </Text>

            {/* Category */}
            {plan.planCategory && (
                <Text
                    fontFamily="Poppins"
                    fontSize={isMobile ? "22px" : "28px"}
                    fontWeight="700"
                    textAlign="center"
                    color={color}
                    mb="8px"
                >
                    {plan.planCategory}
                </Text>
            )}

            {/* Divider accent */}
            <Box w="60px" h="4px" bg={color} mx="auto" mb="16px" borderRadius="2px" />

            {/* Pricing */}
            <Box textAlign="center" mb="16px">
                <Text
                    fontFamily="Poppins"
                    fontSize={isMobile ? "24px" : "28px"}
                    fontWeight="700"
                    color="#333"
                >
                    ₹{(Number(plan.amount) / 100).toLocaleString()}
                    <Text as="span" fontSize={isMobile ? "14px" : "16px"} fontWeight="400">
                        {" "}per {plan.period}
                    </Text>
                </Text>
            </Box>

            {/* Divider */}
            <Box h="1px" bg="gray.200" mb="16px" />

            {/* Coverage */}
            {plan.coverageTitle && (
                <Box textAlign="center" mb="12px">
                    <Text fontFamily="Poppins" fontSize={isMobile ? "14px" : "16px"}>
                        <Text as="span" color={color} fontWeight="600">Coverage: </Text>
                        <Text as="span" fontWeight="700">{plan.coverageTitle}</Text>
                    </Text>
                    {plan.coverageDescription && (
                        <Text
                            fontFamily="Poppins"
                            fontSize={isMobile ? "12px" : "14px"}
                            color="gray.600"
                            mt="4px"
                            whiteSpace="pre-line"
                        >
                            {plan.coverageDescription}
                        </Text>
                    )}
                </Box>
            )}

            {/* Divider */}
            <Box h="1px" bg="gray.200" mb="16px" />

            {/* Includes text + highlights */}
            {plan.includesText && (
                <Text
                    fontFamily="Poppins"
                    fontSize={isMobile ? "12px" : "14px"}
                    fontWeight="600"
                    color={color}
                    textAlign="center"
                    mb="8px"
                >
                    {plan.includesText}
                </Text>
            )}
            {plan.highlights && plan.highlights.length > 0 && (
                <Box textAlign="center" mb="16px">
                    {plan.highlights.map((highlight, idx) => (
                        <Text
                            key={idx}
                            fontFamily="Poppins"
                            fontSize={isMobile ? "12px" : "14px"}
                            color="#333"
                        >
                            {highlight}
                        </Text>
                    ))}
                </Box>
            )}

            {/* Select button */}
            <Button
                w="full"
                variant="outline"
                borderColor={color}
                color={color}
                fontFamily="Poppins"
                fontSize={isMobile ? "14px" : "16px"}
                fontWeight="500"
                borderRadius="8px"
                _hover={{ bg: `${color}`, color: "white" }}
                mt="8px"
            >
                {isSelected ? "Selected" : "Select Plan"}
            </Button>
        </Box>
    );
};

export default PlanCard;
